import { IPossiblePart, IProductTemplate } from "@drone-parts.garphild.pro/interfaces";
import { makeAutoObservable } from "mobx";
import axios from "axios";
import { notification } from "antd";
import RestApiQuery from "../helpers/rest-api-query";
import { cloneDeep } from "lodash";

class TemplatesStorage {
  templates: IProductTemplate[] = [];
  isEditModalOpen = false;
  selectedTemplate: IProductTemplate | null = null;
  partsDictionary: { partType: string, shortName: string[] }[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  saveTemplate = async () => {
    if (!this.selectedTemplate) {
      notification.error({ message: "Template not found" });
      return;
    }
    try {
      await axios
        .post("api/templates", this.selectedTemplate)
        .then(() => {
          this.loadTemplates();
          notification.success({ message: "Template saved" });
          this.editTemplate(false);
          this.selectedTemplate = null;
        });
      notification.success({ message: "Template saved" });
    } catch (error) {
      notification.error({ message: "Failed to save template" });
    }
  }

  loadTemplates = async () => {
    try {
      const data = await RestApiQuery.get<IProductTemplate[]>("api/templates");
      this.templates = data || [];
      notification.success({ message: "Templates loaded" });
    } catch (error) {
      notification.error({ message: "Failed to load templates" });
    }
  }

  addPartGroup = () => {
    if (!this.selectedTemplate) {
      notification.error({ message: "Template not found" });
      return;
    }
    const newPartGroup: IPossiblePart = {
      groupId: "",
      title: "",
      partNames: [],
      targetPrice: 0,
      minPrice: 0,
      maxPrice: 0,
    }
    const newTemplate = {
      ...this.selectedTemplate,
      possibleParts: [
        ...this.selectedTemplate.possibleParts,
        newPartGroup
      ],
    }
    this.selectedTemplate = newTemplate;
  }

  changePartGroup = (indexPart: number, value: IPossiblePart) => {
    const newPartGroup = cloneDeep(value);
    if (!this.selectedTemplate) {
      notification.error({ message: "Template not found" });
      return;
    }
    const template = this.selectedTemplate;
    const newTemplate = {
      ...template,
      possibleParts: [
        ...template.possibleParts.slice(0, indexPart),
        newPartGroup,
        ...template.possibleParts.slice(indexPart + 1)
      ],
    }
    this.selectedTemplate = newTemplate;
  }

  removePartGroup = (indexPart: number) => {
    if (!this.selectedTemplate) {
      notification.error({ message: "Template not found" });
      return;
    }
    const template = this.selectedTemplate;
    const newTemplate = {
      ...template,
      possibleParts: [
        ...template.possibleParts.slice(0, indexPart),
        ...template.possibleParts.slice(indexPart + 1)
      ],
    };
    this.selectedTemplate = newTemplate;
  }

  movePartGroupUp = (indexPart: number) => {
    if (!this.selectedTemplate) {
      notification.error({ message: "Template not found" });
      return;
    }
    const template = this.selectedTemplate;
    const newTemplate = {
      ...template,
      possibleParts: [
        ...template.possibleParts.slice(0, indexPart - 1),
        template.possibleParts[indexPart],
        template.possibleParts[indexPart - 1],
        ...template.possibleParts.slice(indexPart + 1)
      ],
    };
    this.selectedTemplate = newTemplate;
  }

  movePartGroupDown = (indexPart: number) => {
    if (!this.selectedTemplate) {
      notification.error({ message: "Template not found" });
      return;
    }
    const template = this.selectedTemplate
    const newTemplate = {
      ...template,
      possibleParts: [
        ...template.possibleParts.slice(0, indexPart),
        template.possibleParts[indexPart + 1],
        template.possibleParts[indexPart],
        ...template.possibleParts.slice(indexPart + 2)
      ],
    };
    this.selectedTemplate = newTemplate;
  }

  editTemplate = (templateId: string | false) => {
    if (templateId !== false) {
      const template = this.templates.find(t => t._id === templateId);
      if (!template) {
        notification.error({ message: "Template not found" });
        this.isEditModalOpen = false;
        this.selectedTemplate = null;
        return;
      }
      this.selectedTemplate = template;
      this.isEditModalOpen = true;
    } else {
      this.selectedTemplate = null;
      this.isEditModalOpen = false;
    }
  }

  loadPartsDictionary = async () => {
    try {
      const data = await RestApiQuery.get<{ partType: string; shortName: string[] }[]>("api/orders/dictionary/parts");

      this.partsDictionary = data || [];

      notification.success({ message: "Parts dictionary loaded" });
    } catch (error) {
      notification.error({ message: "Failed to load parts dictionary" });
    }
  }

  changeTemplateField = (fieldName: keyof IProductTemplate, value: string) => {
    if (!this.selectedTemplate) {
      return;
    }
    const newTemplate = {
      ...this.selectedTemplate,
      [fieldName]: value,
    }
    this.selectedTemplate = newTemplate
  }

  createTemplate = () => {
    this.selectedTemplate = {
      _id: "",
      title: "",
      type: "",
      description: "",
      currency: "",
      image: "",
      total: 0,
      totalMin: 0,
      totalMax: 0,
      possibleParts: [],
    } as IProductTemplate;
    this.isEditModalOpen = true;
  }

  cloneTemplateForEdit = (templateId: string) => {
    const template = this.templates.find(t => t._id === templateId);
    if (!template) {
      notification.error({ message: "Template not found" });
      return;
    }
    this.selectedTemplate = {
      ...cloneDeep(template),
      _id: "",
    } as IProductTemplate;
    this.isEditModalOpen = true;
  }
}

const templatesStorage = new TemplatesStorage();

export default templatesStorage;
